import { useState } from 'react';

import type { PipelineRun } from '@clm/shared-types';

import { approvePipelineRun, cancelPipelineRun } from '../../api/pipeline-runs';
import { mayAttempt } from '../../auth/permissions';
import { useIdentity } from '../../auth/useIdentity';
import { PIPELINE_FORBIDDEN_COPY } from '../pipeline-status-chips';
import { APPROVE_ROLES, runProblemHead, summariseFilter, toRunProblem, type RunProblem } from './shared';

/**
 * The pending list — `requested` and `approved` rows — CLM-83 (D-83-2, D-83-7).
 *
 * **Approve** dims for an identity without the admin role; **Cancel** is
 * enabled for an admin *or* when the row is the caller's own (`requested_by_upn
 * === identity.username`). Both are display hints; the API decides (NFR-06).
 * `onDone` is the section's `reload()` — no row is patched in place.
 */
export function PendingRuns({ runs, onDone }: { readonly runs: readonly PipelineRun[]; readonly onDone: () => void }) {
  const { identity } = useIdentity();
  const mayApprove = mayAttempt(identity, APPROVE_ROLES);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [problem, setProblem] = useState<RunProblem | null>(null);

  const act = async (run: PipelineRun, action: 'approve' | 'cancel') => {
    setBusyId(run.pipeline_run_id);
    setProblem(null);
    try {
      if (action === 'approve') await approvePipelineRun(run.pipeline_run_id);
      else await cancelPipelineRun(run.pipeline_run_id);
      onDone();
    } catch (error) {
      setProblem(toRunProblem(error));
    } finally {
      setBusyId(null);
    }
  };

  if (runs.length === 0) return <p className="mut">No run is pending.</p>;

  return (
    <div role="group" aria-label="Pending runs">
      <ul className="plain">
        {runs.map((run) => {
          const own = identity.username !== null && run.requested_by_upn === identity.username;
          const mayCancel = mayApprove || own;
          const busy = busyId === run.pipeline_run_id;
          return (
            <li key={run.pipeline_run_id} data-status={run.status}>
              <p>
                <strong>{run.status}</strong> — requested by {run.requested_by_upn}{' '}
                <time dateTime={run.requested_utc}>{run.requested_utc}</time>
                {run.approved_by_upn === null ? null : <> · approved by {run.approved_by_upn}</>}
              </p>
              <p className="mut">
                {summariseFilter(run.filter)}
                {run.reason === null ? null : <> · {run.reason}</>}
              </p>
              <div className="vacts">
                {run.status === 'requested' ? (
                  <button
                    type="button"
                    className="btn pri sm"
                    disabled={!mayApprove || busy}
                    title={mayApprove ? undefined : 'Admin role required'}
                    aria-label={`Approve run ${run.pipeline_run_id}`}
                    onClick={() => void act(run, 'approve')}
                  >
                    Approve
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn sm"
                  disabled={!mayCancel || busy}
                  title={mayCancel ? undefined : 'Only the requester or an admin may cancel'}
                  aria-label={`Cancel run ${run.pipeline_run_id}`}
                  onClick={() => void act(run, 'cancel')}
                >
                  Cancel
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {problem === null ? null : (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>{runProblemHead(problem, PIPELINE_FORBIDDEN_COPY)}</strong>
          </p>
          {problem.field_errors.map((entry) => (
            <p key={entry.path} className="mut">
              {entry.message}
            </p>
          ))}
          <button type="button" className="btn" onClick={onDone}>
            Reload
          </button>
        </div>
      )}
    </div>
  );
}
